import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../../context/notificationContext";
import useI18n from "../../../../../../hooks/useTranslations";
import { useApi } from "../../../../../../http/api";
import {
  AdminRoofType,
  UpdateAdminRoofTypeNewImageBody,
  UpdateAdminRoofTypeRemoveImageBody,
} from "../../../../../../http/patrol/types/roofTypes";
import { useRoofTypesApi } from "../../../../../../http/patrol/useRoofTypes";
import Form from "../../../../../ui/Form";
import FormField from "../../../../../ui/FormField";
import FormFieldsContainer from "../../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../../ui/ImageUploader";
import Input from "../../../../../ui/Input";
import Numeric from "../../../../../ui/Numeric";
import Switch from "../../../../../ui/Switch";
import TranslatableField from "../../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";

interface UpdateRoofTypeForm {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  hasHips: boolean;
  canBeCustom: boolean;
  canBeMultiple: boolean;
}

interface RoofTypeDetailsProps {
  roofType?: AdminRoofType;
  disabled?: boolean;
}

const RoofTypeDetails: FC<RoofTypeDetailsProps> = ({ roofType, disabled }: RoofTypeDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const roofTypesApi = useRoofTypesApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: roofType?.image,
    isLoading: !roofType || (!!roofType && !roofType?.id),
    disabled,
  });

  const defaultValues: UpdateRoofTypeForm = {
    name: roofType?.name ?? null,
    nameIt: roofType?.nameIt ?? null,
    nameDe: roofType?.nameDe ?? null,
    nameEs: roofType?.nameEs ?? null,
    nameFr: roofType?.nameFr ?? null,
    order: roofType?.order ?? 50,
    visibility: roofType?.visibility ?? "hidden",
    hasHips: roofType?.hasHips ?? false,
    canBeCustom: roofType?.canBeCustom ?? false,
    canBeMultiple: roofType?.canBeMultiple ?? false,
  };

  const submit = async (body: UpdateAdminRoofTypeNewImageBody | UpdateAdminRoofTypeRemoveImageBody) => {
    try {
      await roofTypesApi.updateAdminRoofType(roofType?.id!, body);
      onSave();
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = () => async (formData: UpdateRoofTypeForm) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name?.trim()!,
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
      hasHips: formData.hasHips,
      canBeCustom: formData.canBeCustom,
      canBeMultiple: formData.canBeMultiple,
    };

    if (newImage) {
      try {
        const signedUrl = await roofTypesApi.generateUploadImageUrl(roofType?.id!, newImage.name, newImage.type);
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateAdminRoofTypeNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateAdminRoofTypeRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  return (
    <Form<UpdateRoofTypeForm>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [roofType]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
        }, [register]);

        const values = watch();

        const hasHips = !!values.hasHips;
        const canBeCustom = !!values.canBeCustom;
        const canBeMultiple = !!values.canBeMultiple;

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="name"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.name}
                  >
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled}
                      {...register("name", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <FormField label={i18n.translation.common.hasHips} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={hasHips}
                  disabled={disabled}
                  onChange={(checked) => setValue("hasHips", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.canBeCustom} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={canBeCustom}
                  disabled={disabled}
                  onChange={(checked) => setValue("canBeCustom", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.canBeMultiple} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={canBeMultiple}
                  disabled={disabled}
                  onChange={(checked) => setValue("canBeMultiple", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default RoofTypeDetails;
